(ns app.front.view.group.admin.fees.variable.variable-subs
  (:require [app.domain.fee.formula :as formula]
            [app.domain.fee.model :as fee-model]
            [app.front.entities :as entities]
            [app.front.view.group.admin.fees.template.template-events :as ft-events]
            [app.front.view.group.admin.fees.template.template-subs :as ft-subs]
            [app.front.view.group.admin.fees.util.fee-utils :as fee-utils]
            [app.front.view.group.admin.fees.util.fee-variable :as fee-variable]
            [app.front.view.group.admin.fees.variable.variable-events :as fv-events]
            [app.front.view.group.admin.form-field-selection :as ff-selection]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.feature-flags :as ff]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::creating-new?
  (fn [db _]
    (= (-> db ::fv-events/fee-variables :selected-fee-variable) :new)))


(rf/reg-sub ::unsaved-changes
  (fn [db _]
    (-> db ::fv-events/fee-variables :unsaved-changes)))

(rf/reg-sub ::fee-variable-for-edit
  :<- [::selected-fee-variable]
  (fn [fee-variable _]
    (fee-utils/strings-map [[:fee-variable/titles :fee-variable/title]]
                           fee-variable)))

(rf/reg-sub ::current-variable-id
  :<- [::selected-fee-variable]
  :<- [::creating-new?]
  fee-utils/current-id-handler)

(rf/reg-sub ::current-variable-changes
  :<- [::unsaved-changes]
  :<- [::current-variable-id]
  fee-utils/changes-handler)

(rf/reg-sub ::fee-variable-attrs-with-changes
  :<- [::fee-variable-for-edit]
  :<- [::current-variable-changes]
  (fn [[fee-variable changes] _]
    (fee-utils/get-props-with-changes fee-variable
                                      fee-variable/all-fields
                                      changes)))

(rf/reg-sub ::fee-schedule
  (fn [db _]
    (-> db ::ft-events/fee-templates :fee-schedule)))


(rf/reg-sub ::fee-variables
  :<- [::entities/context]
  :<- [::fee-schedule]
  (fn [[ctx fee-schedule] _]
    (entities/find-entities ctx {:entity/parent (:db/id fee-schedule)
                                 :entity/type   :entity.type/fee-variable})))

(rf/reg-sub ::usable-variables
  :<- [::selected-fee-variable-id]
  :<- [::fee-variables]
  (fn [[variable-id fee-variables] _]
    (->> fee-variables
         (filter (fn [fee-variable]
                   (not= variable-id (:db/id fee-variable)))))))

(rf/reg-sub ::fee-variable-list
  :<- [::fee-variables]
  :<- [::selected-fee-variable-id]
  :<- [:language]
  (fn [[fee-variables selected-id lang] _]
    (->> fee-variables
         (sort-by #(-> %
                       (fee-model/fee-variable-title lang)
                       (or "")
                       str/lower-case))
         (map (fn [var] (p/assoc-if {:id    (:db/id var)
                                     :title (fee-model/fee-variable-title var lang)}
                                    :selected? (= (:db/id var) selected-id)))))))


(rf/reg-sub ::selected-fee-variable-id
  (fn [db _]
    (-> db ::fv-events/fee-variables :selected-fee-variable)))


(rf/reg-sub ::selected-fee-variable
  :<- [::selected-fee-variable-id]
  :<- [::fee-variables]
  (fn [[id fee-variables] _]
    (p/find-first fee-variables #(= (:db/id %) id))))


(rf/reg-sub ::fee-variable-errors
  (fn [db _]
    (get-in db [::fv-events/fee-variables :errors])))


(rf/reg-sub ::save-disabled?
  :<- [::selected-fee-variable]
  :<- [::unsaved-changes]
  :<- [::current-variable-id]
  :<- [::variable-formula]
  :<- [::fee-variable-errors]
  (fn [[fee-variable unsaved-changes variable-id formula fee-variable-errors] _]
    (let [changes         (get unsaved-changes variable-id)
          get-value       (fee-utils/get-value-fn fee-variable changes)
          has-value?      #(not (i18n-model/blank? (get-value %)))
          fields-missing? (not (every? has-value? fee-variable/required-fields))
          changes?        (some? changes)
          type-has-value? (cond
                            (= (get-value :fee-variable/source-type) :fee-variable.source-type/formula)
                            (and (has-value? :fee-variable/fee-formula) (not (:invalid? formula)))

                            (= (get-value :fee-variable/source-type) :fee-variable.source-type/fee)
                            (has-value? :fee-variable/fee-template)

                            :else
                            (and (has-value? :fee-variable/source-field)
                                 (not= :none (get-value :fee-variable/source-field))))
          cycle-error?    (get-in fee-variable-errors [variable-id :cycle])
          save-enabled?   (and changes? type-has-value? (not fields-missing?) (not cycle-error?))]
      (not save-enabled?))))


(rf/reg-sub ::raw-selected-form
  (fn [db _]
    (-> db ::fv-events/fee-variables :selected-form)))


(defn field->form [form-fields field]
  (->> form-fields
       vals
       flatten
       (filter #(= field (:field %)))
       first
       :form-label))


(rf/reg-sub ::selected-form
  :<- [::raw-selected-form]
  :<- [::ff-selection/raw-form-fields]
  :<- [::creating-new?]
  :<- [::selected-fee-variable]
  (fn [[raw-selected-form raw-form-fields creating-new? selected-fee-variable]]
    (if creating-new?
      raw-selected-form
      (or raw-selected-form
          (field->form raw-form-fields (:fee-variable/source-field selected-fee-variable))))))


(defn numeric-field? [field] (-> field :type (= :number)))

(defn allowed-field? [field]
  (or (numeric-field? field)
      (contains? #{:checkbox :select :radio-button-group :area-input :personal-id} (-> field :tag))))


(rf/reg-sub ::forms
  :<- [::ff-selection/raw-form-fields]
  (fn [raw-form-fields _]
    (let [alpha-fields-enabled? (ff/enabled? :feature-flag.id/fee-alpha-fields)]
      (->> (if alpha-fields-enabled?
             raw-form-fields
             (filter (fn [[_ fields]] (some numeric-field? fields)) raw-form-fields))
           keys
           sort))))


(rf/reg-sub ::form-fields
  :<- [::ff-selection/raw-form-fields]
  :<- [::selected-form]
  (fn [[raw-form-fields selected-form] _]
    (let [alpha-fields-enabled? (ff/enabled? :feature-flag.id/fee-alpha-fields)
          form-fields           (->> (get raw-form-fields selected-form)
                                     distinct)]
      (if alpha-fields-enabled?
        (filter allowed-field? form-fields)
        (filter numeric-field? form-fields)))))


(rf/reg-sub ::fee-templates-using-variable
  :<- [::ft-subs/fee-templates]
  (fn [fee-templates [_ fee-variable]]
    (when-some [identifier (:fee-variable/identifier fee-variable)]
      (filter
        (fn [template]
          (some
            #(= % identifier)
            (set/union
              (some-> template :fee-template/unit-price-formula formula/tokenize set)
              (some-> template :fee-template/quantity-formula formula/tokenize set))))
        fee-templates))))

(rf/reg-sub ::fee-variables-using-variable
  :<- [::fee-variables]
  (fn [fee-variables [_ fee-variable]]
    (when-some [identifier (:fee-variable/identifier fee-variable)]
      (filter
        (fn [var]
          (when (= (:fee-variable/source-type var) :fee-variable.source-type/formula)
            (some
              #(= % identifier)
              (formula/tokenize (:fee-variable/fee-formula var)))))
        fee-variables))))

(rf/reg-sub ::current-formula-status
  (fn [db _]
    (let [variable-id @(rf/subscribe [::current-variable-id])
          status      (get-in db [::fv-events/fee-variables :formula-status variable-id])]
      (if (some? status) status :not-checked))))

(rf/reg-sub ::current-formula-message
  (fn [db _]
    (let [variable-id @(rf/subscribe [::current-variable-id])]
      (get-in db [::fv-events/fee-variables :formula-message variable-id]))))

(rf/reg-sub ::variable-formula
  :<- [::selected-fee-variable]
  :<- [::fee-variables]
  :<- [::current-variable-changes]
  :<- [::current-formula-status]
  :<- [::current-formula-message]
  :<- [:tr]
  (fn [[fee-variable fee-variables changes status message tr] _]
    (let [formula (or (:fee-variable/fee-formula changes)
                      (:fee-variable/fee-formula fee-variable))]
      (assoc (formula/parse-formula formula fee-variables status message tr)
             :loading? (= :loading status)))))
